import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import OnboardingPage from "./pages/OnboardingPage";
import DashboardPage from "./pages/DashboardPage";
import DiscoverPage from "./pages/DiscoverPage";
import MatchesPage from "./pages/MatchesPage";
import ChatPage from "./pages/ChatPage";
import QuizzesPage from "./pages/QuizzesPage";
import QuizTakePage from "./pages/QuizTakePage";
import EducationPage from "./pages/EducationPage";
import ProfilePage from "./pages/ProfilePage";
import AdminPage from "./pages/AdminPage";
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-soft text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Onboarding Guard
const OnboardingGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-soft text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }
  
  // Check if user needs to pay first
  if (user && !user.is_paid_member) {
    return <Navigate to="/payment" replace />;
  }
  
  if (user && !user.is_onboarded) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

// Payment Guard - ensures user is logged in but allows unpaid users
const PaymentGuard = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-soft text-primary text-xl font-serif">Loading...</div>
      </div>
    );
  }
  
  if (user?.is_paid_member) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/auth" element={user ? <Navigate to="/dashboard" replace /> : <AuthPage />} />
      
      {/* Onboarding */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      
      {/* Payment */}
      <Route path="/payment" element={
        <ProtectedRoute>
          <PaymentGuard>
            <PaymentPage />
          </PaymentGuard>
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <PaymentCancelPage />
        </ProtectedRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <DashboardPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/discover" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <DiscoverPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/matches" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <MatchesPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/chat/:matchId" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <ChatPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/quizzes" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <QuizzesPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/quizzes/:quizId" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <QuizTakePage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/education" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <EducationPage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <OnboardingGuard>
            <ProfilePage />
          </OnboardingGuard>
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={<AdminPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
